import { useNavigate, useSearch } from '@tanstack/react-router';
import { useAllHighscores } from '@/hooks/useHighscores';
import { formatNumber, formatDate } from '@/lib/utils';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/i18n';

export function LeaderboardPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/leaderboard' });
  const { game = 'all', page = 1, limit = 20 } = search;
  const { t } = useTranslation();

  const GAME_OPTIONS = [
    { value: 'all', label: t.leaderboard.allGames },
    { value: 'cabinet-transformer', label: 'Cabinet Transformer' },
    { value: 'automation-drop', label: 'Automation Drop' },
  ];

  const { data: scores, isLoading, error } = useAllHighscores({
    game: game === 'all' ? undefined : game,
    limit,
    offset: (page - 1) * limit,
  });

  const handleGameChange = (newGame: string) => {
    navigate({
      to: '/leaderboard',
      search: { game: newGame as 'all' | 'cabinet-transformer' | 'automation-drop', page: 1, limit },
    });
  };

  return (
    <div className="animate-fade-in max-w-4xl mx-auto">
      <div className="text-center mb-8 sm:mb-12">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-text-primary mb-3 sm:mb-4">
          🏆 {t.leaderboard.title}
        </h1>
        <p className="text-text-secondary text-sm sm:text-base">
          {t.leaderboard.subtitle}
        </p>
      </div>

      {/* Filter - touch friendly buttons */}
      <div className="flex flex-wrap gap-2 mb-6 sm:mb-8 justify-center px-2">
        {GAME_OPTIONS.map((option) => (
          <button
            key={option.value}
            onClick={() => handleGameChange(option.value)}
            className={cn(
              'px-4 py-2.5 rounded-full text-sm font-medium transition-all min-h-[44px]',
              game === option.value
                ? 'bg-ukonnx-green text-bg-deep'
                : 'bg-border-dark text-text-secondary hover:text-text-primary active:scale-95'
            )}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* Table - with mobile card view */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-6 sm:p-8 text-center text-text-muted">
            {t.common.loading}
          </div>
        ) : error ? (
          <div className="p-6 sm:p-8 text-center text-accent-red">
            {t.common.error}
          </div>
        ) : scores && scores.length > 0 ? (
          <>
            {/* Desktop Table - hidden on mobile */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-bg-deep border-b border-border-dark">
                  <tr>
                    <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-sm font-semibold text-text-secondary">
                      {t.common.rank}
                    </th>
                    <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-sm font-semibold text-text-secondary">
                      {t.leaderboard.player}
                    </th>
                    <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-sm font-semibold text-text-secondary">
                      {t.leaderboard.game}
                    </th>
                    <th className="px-4 sm:px-6 py-3 sm:py-4 text-right text-sm font-semibold text-text-secondary">
                      {t.common.score}
                    </th>
                    <th className="px-4 sm:px-6 py-3 sm:py-4 text-right text-sm font-semibold text-text-secondary hidden md:table-cell">
                      {t.leaderboard.date}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-dark">
                  {scores.map((score, index) => {
                    const rank = (page - 1) * limit + index + 1;
                    return (
                      <tr 
                        key={score.id}
                        className="hover:bg-white/5 transition-colors"
                      >
                        <td className="px-4 sm:px-6 py-3 sm:py-4">
                          <RankBadge rank={rank} />
                        </td>
                        <td className="px-4 sm:px-6 py-3 sm:py-4">
                          <span className="font-medium text-text-primary">
                            {score.player_name}
                          </span>
                        </td>
                        <td className="px-4 sm:px-6 py-3 sm:py-4">
                          <GameBadge gameId={score.game_id} />
                        </td>
                        <td className="px-4 sm:px-6 py-3 sm:py-4 text-right">
                          <span className="font-bold text-text-primary">
                            {formatNumber(score.score)}
                          </span>
                        </td>
                        <td className="px-4 sm:px-6 py-3 sm:py-4 text-right text-text-muted text-sm hidden md:table-cell">
                          {formatDate(score.created_at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View - visible only on small screens */}
            <div className="sm:hidden divide-y divide-border-dark">
              {scores.map((score, index) => {
                const rank = (page - 1) * limit + index + 1;
                return (
                  <div 
                    key={score.id}
                    className="p-4 flex items-center gap-3"
                  >
                    <div className="shrink-0 w-10 text-center">
                      <RankBadge rank={rank} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-text-primary truncate">
                        {score.player_name}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <GameBadge gameId={score.game_id} />
                        <span className="text-text-muted text-xs">
                          {formatDate(score.created_at)}
                        </span>
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <span className="font-bold text-text-primary text-lg">
                        {formatNumber(score.score)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="p-6 sm:p-8 text-center text-text-muted">
            {t.leaderboard.noScoresYet}
          </div>
        )}
      </div>

      {/* Pagination - touch friendly */}
      {scores && scores.length >= limit && (
        <div className="flex justify-center items-center gap-2 sm:gap-4 mt-6 sm:mt-8">
          <button
            onClick={() => navigate({
              to: '/leaderboard',
              search: { game, page: Math.max(1, page - 1), limit },
            })}
            disabled={page === 1}
            className="btn-secondary disabled:opacity-50 min-h-[44px] px-4 sm:px-6"
          >
            <span className="hidden sm:inline">{t.leaderboard.previous}</span>
            <span className="sm:hidden">←</span>
          </button>
          <span className="flex items-center text-text-secondary text-sm sm:text-base px-2">
            {t.leaderboard.page} {page}
          </span>
          <button
            onClick={() => navigate({
              to: '/leaderboard',
              search: { game, page: page + 1, limit },
            })}
            className="btn-secondary min-h-[44px] px-4 sm:px-6"
          >
            <span className="hidden sm:inline">{t.leaderboard.next}</span>
            <span className="sm:hidden">→</span>
          </button>
        </div>
      )}
    </div>
  );
}

function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) {
    return <span className="text-xl sm:text-2xl">🥇</span>;
  }
  if (rank === 2) {
    return <span className="text-xl sm:text-2xl">🥈</span>;
  }
  if (rank === 3) {
    return <span className="text-xl sm:text-2xl">🥉</span>;
  }
  return <span className="text-text-muted font-medium text-sm sm:text-base">#{rank}</span>;
}

function GameBadge({ gameId }: { gameId: string }) {
  const games: Record<string, { label: string; color: string }> = {
    'cabinet-transformer': { label: 'Cabinet', color: 'bg-ukonnx-green/20 text-ukonnx-green' },
    'automation-drop': { label: 'Automation', color: 'bg-blue-500/20 text-blue-400' },
  };

  const game = games[gameId] || { label: gameId, color: 'bg-border-dark text-text-secondary' };

  return (
    <span className={cn(
      'inline-block px-2 py-0.5 sm:py-1 rounded text-xs font-medium',
      game.color
    )}>
      {game.label}
    </span>
  );
}
